import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { Typography } from 'app/components/ui';
import { LabeledSection } from './LabeledSection';
import { i18n } from 'shared/i18n';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { api } from 'shared/api/client';
import {
  CalculationGroupResponse,
  CalculationRow,
  CalculationRuleSummandInput,
  TagWithMetadata,
  useDeleteCalculationRule,
  useSeedCalculationRule,
  useUpdateCalculationRule,
} from 'entities/tag';
import { Taxonomy } from 'app/components/editor/sidebar/tags/taxonomy/Taxonomy';
import { buildUsedConceptRoles } from 'app/components/editor/sidebar/tags/taxonomy/usedConceptRoles';

export interface CalculationsAuthoring {
  documentSuperId: number;
  parentConceptId: string;
  selectedConceptName: string;
  documentTags?: TagWithMetadata[];
  onPickTerms?: (group: CalculationGroupResponse) => void;
  onCreateSum?: () => void;
}

interface CalculationsSectionProps {
  calculationGroups: CalculationGroupResponse[] | null | undefined;
  isLoading?: boolean;
  isFetching?: boolean;
  isPreviousData?: boolean;
  authoring?: CalculationsAuthoring;
}

const actionButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontSize: 10,
  fontWeight: 600,
  color: '#5a6b7d',
  textDecoration: 'underline',
};

const groupCaptionStyle: React.CSSProperties = {
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  fontWeight: 600,
};

const formatAmount = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 2 });

const isDebitCredit = (balance?: string | null) => balance === 'debit' || balance === 'credit';

const balanceMarker = (balance?: string | null) =>
  balance === 'debit'
    ? i18n('tag:calculations.balance.debit')
    : balance === 'credit'
    ? i18n('tag:calculations.balance.credit')
    : i18n('tag:calculations.balance.unknown');

// Mirrors the backend balance/weight guard (CALC_RULE_BALANCE_WEIGHT).
const isWeightLocked = (parentBalance?: string | null, rowBalance?: string | null) => isDebitCredit(parentBalance) && isDebitCredit(rowBalance);

const arithmeticPillStyle = (matches: boolean): React.CSSProperties => ({
  padding: '1px 6px',
  borderRadius: 3,
  fontSize: 10,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  backgroundColor: matches ? '#e3efe5' : '#f6e7e7',
  color: matches ? '#3d7a4f' : '#a14242',
});

function summandsFromRows(rows: CalculationRow[]): CalculationRuleSummandInput[] {
  return rows
    .filter((r) => !r.isParent && r.conceptId)
    .map((r) => ({
      summandConceptId: r.conceptId as string,
      weight: r.weight,
      ...(r.dimQualifiers && r.dimQualifiers.length > 0
        ? { dimQualifiers: r.dimQualifiers.map((q) => ({ axisConceptId: q.axisConceptId, memberConceptId: q.memberConceptId })) }
        : {}),
    }));
}

export const CalculationsSection = ({ calculationGroups, isLoading, isFetching, isPreviousData, authoring }: CalculationsSectionProps) => {
  const { t } = useTranslation(['common', 'tag']);

  const seedRule = useSeedCalculationRule();
  const deleteRule = useDeleteCalculationRule();
  const updateRule = useUpdateCalculationRule();
  const anyMutating = seedRule.isPending || deleteRule.isPending || updateRule.isPending;
  const [pickerForGroup, setPickerForGroup] = useState<number | null>(null);
  const [pickedTermConcept, setPickedTermConcept] = useState<string | null>(null);

  const usedConceptRoles = useMemo(() => buildUsedConceptRoles(authoring?.documentTags ?? []), [authoring?.documentTags]);

  if (isLoading || isPreviousData) {
    return (
      <LabeledSection label={t('tag:calculations.title')}>
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ height: 12, borderRadius: 3, backgroundColor: '#e9e9ee' }} />
          ))}
        </div>
      </LabeledSection>
    );
  }

  const hasCalculations = calculationGroups && calculationGroups.length > 0;

  const isOwnGroup = (group: CalculationGroupResponse) => {
    const parentRow = group.rows.find((r) => r.isParent);
    return !!authoring && !!parentRow?.conceptId && parentRow.conceptId === authoring.parentConceptId;
  };

  const onSeed = (group: CalculationGroupResponse) => {
    if (!authoring || !group.linkroleUri) return;
    seedRule.mutate({ documentSuperId: authoring.documentSuperId, parentConceptId: authoring.parentConceptId, linkroleUri: group.linkroleUri });
  };

  const onReset = (group: CalculationGroupResponse) => {
    if (!authoring || !group.ruleId) return;
    deleteRule.mutate({ ruleId: group.ruleId, documentSuperId: authoring.documentSuperId });
  };

  const onRemoveTerm = (group: CalculationGroupResponse, row: CalculationRow) => {
    if (!authoring || !group.ruleId) return;
    const summands = summandsFromRows(group.rows).filter((s) => s.summandConceptId !== row.conceptId);
    if (summands.length === 0) {
      deleteRule.mutate({ ruleId: group.ruleId, documentSuperId: authoring.documentSuperId });
      return;
    }
    updateRule.mutate({ ruleId: group.ruleId, documentSuperId: authoring.documentSuperId, summands });
  };

  const onToggleWeight = (group: CalculationGroupResponse, row: CalculationRow) => {
    if (!authoring || !group.ruleId) return;
    const summands = summandsFromRows(group.rows).map((s) => (s.summandConceptId === row.conceptId ? { ...s, weight: -(s.weight ?? row.weight) } : s));
    updateRule.mutate(
      { ruleId: group.ruleId, documentSuperId: authoring.documentSuperId, summands },
      { onSuccess: () => dispatchMessage(EventType.ADD_MESSAGE, { type: 'success', text: t('tag:calculations.weight-toggled'), removeTime: 4000 }) }
    );
  };

  const onAddTermByName = async (group: CalculationGroupResponse, conceptName: string) => {
    if (!authoring || !group.ruleId) return;
    let conceptId: string | undefined;
    try {
      const { data } = await api.GET('/taxonomy/concepts', {
        params: { query: { name: conceptName, documentSuperId: String(authoring.documentSuperId) } },
      });
      conceptId = data?.id;
    } catch {
      return;
    }
    if (!conceptId) return;
    const existing = summandsFromRows(group.rows);
    if (existing.some((s) => s.summandConceptId === conceptId)) return;
    updateRule.mutate({
      ruleId: group.ruleId,
      documentSuperId: authoring.documentSuperId,
      summands: [...existing, { summandConceptId: conceptId }],
    });
  };

  const ownsAnyGroup = !!calculationGroups?.some((g) => isOwnGroup(g));
  const showCreateSum = !!authoring?.onCreateSum && !ownsAnyGroup;

  return (
    <LabeledSection label={t('tag:calculations.title')}>
      {!hasCalculations ? (
        <div className="flex flex-col gap-1">
          <Typography variant="body-5">{t('tag:calculations.none')}</Typography>
          {showCreateSum && (
            <button type="button" style={actionButtonStyle} disabled={anyMutating} onClick={() => authoring?.onCreateSum?.()}>
              {t('tag:calculations.create-sum')}
            </button>
          )}
        </div>
      ) : (
        <div style={{ opacity: isFetching ? 0.85 : 1, transition: 'opacity 120ms ease' }} aria-busy={isFetching ? 'true' : undefined}>
          {calculationGroups.map((group, groupIdx) => {
            const editable = isOwnGroup(group);
            const isDoc = group.source === 'document';
            const parentBalance = group.rows.find((r) => r.isParent)?.balance ?? null;
            return (
              <div className="flex flex-col gap-2 my-1" key={groupIdx}>
                {isDoc && (
                  <Typography variant="body-1" style={groupCaptionStyle}>
                    {group.origin === 'TAXONOMY_SEEDED' ? t('tag:calculations.source.seeded') : t('tag:calculations.source.filer')}
                  </Typography>
                )}
                {group.rows
                  .filter((row) => !row.isParent)
                  .map((row, idx) => {
                    const untagged = row.tagId == null && row.value == null;
                    const weightLocked = isWeightLocked(parentBalance, row.balance);
                    return (
                      <div className="flex flex-col gap-1" key={idx}>
                        <div
                          className="flex flex-row justify-between"
                          title={untagged ? t('tag:calculations.untagged-term') : undefined}
                          style={untagged ? { opacity: 0.5, fontStyle: 'italic', color: '#9a99a0' } : undefined}
                        >
                          <Typography variant="body-3" className="calculations-label" style={{ minWidth: 0, color: untagged ? '#9a99a0' : undefined }}>
                            {editable && isDoc ? (
                              <button
                                type="button"
                                style={{
                                  ...actionButtonStyle,
                                  textDecoration: 'none',
                                  marginRight: 2,
                                  ...(weightLocked ? { cursor: 'not-allowed', color: '#b8bcc4', textDecoration: 'none' } : {}),
                                }}
                                title={weightLocked ? t('tag:calculations.error.balance-weight') : t('tag:calculations.toggle-weight')}
                                disabled={updateRule.isPending || weightLocked}
                                onClick={() => onToggleWeight(group, row)}
                              >
                                {row.weight >= 0 ? '+' : '-'}
                              </button>
                            ) : (
                              <>{row.weight >= 0 ? '+' : '-'} </>
                            )}
                            {`(${balanceMarker(row.balance)}) `}
                            {row.label || row.conceptName.split(':')[1]}
                            {row.isSum ? ' Σ' : ''}
                          </Typography>
                          <span className="flex flex-row" style={{ gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            <Typography variant="body-3" color={row.warning ? '#a14242' : untagged ? '#9a99a0' : undefined} style={{ whiteSpace: 'nowrap' }}>
                              {row.warning === 'SCOPE_CONFLICT'
                                ? t('tag:calculations.scope-conflict')
                                : row.warning === 'DUPLICATE_FACT'
                                ? t('tag:calculations.duplicate-fact')
                                : row.value ?? (untagged ? '–' : '')}
                            </Typography>
                            {editable && isDoc && (
                              <button
                                type="button"
                                style={{ ...actionButtonStyle, textDecoration: 'none' }}
                                title={t('tag:calculations.remove-term')}
                                disabled={updateRule.isPending || deleteRule.isPending}
                                onClick={() => onRemoveTerm(group, row)}
                              >
                                ✕
                              </button>
                            )}
                          </span>
                        </div>
                        {row.dimQualifiers && row.dimQualifiers.length > 0 && (
                          <div className="flex flex-row" style={{ flexWrap: 'wrap', gap: 4, marginLeft: 16 }}>
                            {row.dimQualifiers.map((q, qIdx) => (
                              <span
                                key={qIdx}
                                title={`${q.axisQName} = ${q.memberQName}`}
                                style={{
                                  padding: '1px 6px',
                                  borderRadius: 3,
                                  fontSize: 10,
                                  fontWeight: 500,
                                  backgroundColor: '#e1e7ed',
                                  color: '#37353b',
                                }}
                              >
                                {q.axisQName.split(':')[1] ?? q.axisQName}: {q.memberQName.split(':')[1] ?? q.memberQName}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                <hr className="border-t border-gray-600 my-2" />
                {group.rows
                  .filter((row) => row.isParent)
                  .map((row, idx) => (
                    <div className="flex flex-row justify-between" key={`parent-${idx}`}>
                      <Typography variant="body-3" fontWeight="semibold" className="calculations-label" style={{ minWidth: 0 }}>
                        {`(${balanceMarker(row.balance)}) `}
                        {row.label || row.conceptName.split(':')[1]}
                      </Typography>
                      <Typography variant="body-3" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {row.value ?? ''}
                      </Typography>
                    </div>
                  ))}
                {group.arithmetic && (
                  <div className="flex flex-row" style={{ justifyContent: 'flex-end' }}>
                    <span
                      style={arithmeticPillStyle(group.arithmetic.matches)}
                      title={group.arithmetic.matches ? t('tag:calculations.arithmetic-match') : t('tag:calculations.arithmetic-mismatch')}
                    >
                      {group.arithmetic.matches
                        ? `✓ ${formatAmount(group.arithmetic.computed)}`
                        : `⚠ Σ ${formatAmount(group.arithmetic.computed)} ≠ ${formatAmount(group.arithmetic.parentValue)}`}
                    </span>
                  </div>
                )}
                {editable && (
                  <div className="flex flex-row" style={{ gap: 12 }}>
                    {!isDoc && group.linkroleUri && (
                      <button type="button" style={actionButtonStyle} disabled={seedRule.isPending} onClick={() => onSeed(group)}>
                        {t('tag:calculations.edit')}
                      </button>
                    )}
                    {isDoc && authoring?.onPickTerms && (
                      <button type="button" style={actionButtonStyle} disabled={anyMutating} onClick={() => authoring.onPickTerms?.(group)}>
                        {t('tag:calculations.pick-terms')}
                      </button>
                    )}
                    {isDoc && group.origin === 'TAXONOMY_SEEDED' && (
                      <button type="button" style={actionButtonStyle} disabled={deleteRule.isPending} onClick={() => onReset(group)}>
                        {t('tag:calculations.reset')}
                      </button>
                    )}
                    {isDoc && group.origin === 'FILER' && (
                      <button type="button" style={actionButtonStyle} disabled={deleteRule.isPending} onClick={() => onReset(group)}>
                        {t('tag:calculations.delete')}
                      </button>
                    )}
                    {isDoc && (
                      <button type="button" style={actionButtonStyle} disabled={anyMutating} onClick={() => setPickerForGroup(groupIdx)}>
                        {t('tag:calculations.add-term')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {hasCalculations && showCreateSum && (
        <div className="flex flex-row" style={{ marginTop: 4 }}>
          <button type="button" style={actionButtonStyle} disabled={anyMutating} onClick={() => authoring?.onCreateSum?.()}>
            {t('tag:calculations.create-sum')}
          </button>
        </div>
      )}

      <BootstrapModal
        show={pickerForGroup !== null}
        onHide={() => {
          setPickerForGroup(null);
          setPickedTermConcept(null);
        }}
        size="lg"
        centered
      >
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>{t('tag:calculations.add-term')}</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body style={{ padding: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '65vh' }}>
            <Taxonomy
              selectedTagId={pickedTermConcept}
              isContentMarked={false}
              usedConceptRoles={usedConceptRoles}
              documentTags={authoring?.documentTags ?? []}
              hideExtensions={false}
              hideFooter
              onSelectionChange={(tag) => setPickedTermConcept(tag?.id ?? null)}
            />
          </div>
        </BootstrapModal.Body>
        <BootstrapModal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setPickerForGroup(null);
              setPickedTermConcept(null);
            }}
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!pickedTermConcept || pickerForGroup === null || updateRule.isPending}
            onClick={() => {
              if (pickerForGroup !== null && pickedTermConcept && calculationGroups?.[pickerForGroup]) {
                onAddTermByName(calculationGroups[pickerForGroup], pickedTermConcept);
              }
              setPickerForGroup(null);
              setPickedTermConcept(null);
            }}
          >
            {t('common:apply')}
          </button>
        </BootstrapModal.Footer>
      </BootstrapModal>
    </LabeledSection>
  );
};
